/**
 * The React layer over i18n-core.ts: the provider is mounted in
 * entry.server/entry.client — above the router — so every surface (error
 * boundaries included) can translate, and switching locale is a document
 * reload by design (see routes/resources/locale.tsx).
 *
 * Components import everything from here (the core surface is re-exported);
 * plain .ts modules import i18n-core.ts or i18n.server.ts instead, because the
 * worker's bare-node loader cannot load .tsx (worker/index.test.ts enforces
 * that its graph stays .tsx-free).
 */
import { createContext, useContext, useMemo, type ReactNode } from 'react'
import {
	makeTranslator,
	type Locale,
	type Messages,
	type Translator,
} from './i18n-core.ts'

export * from './i18n-core.ts'

const I18nContext = createContext<{
	locale: Locale
	messages: Messages
} | null>(null)

export function I18nProvider({
	locale,
	messages,
	children,
}: {
	locale: Locale
	messages: Messages
	children: ReactNode
}) {
	const value = useMemo(() => ({ locale, messages }), [locale, messages])
	return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}

/** Active locale; 'nl' outside the provider (only tests render without one). */
export function useLocale(): Locale {
	return useContext(I18nContext)?.locale ?? 'nl'
}

export function useT(): Translator {
	const ctx = useContext(I18nContext)
	const translator = useMemo(
		() => (ctx ? makeTranslator(ctx.locale, ctx.messages) : null),
		[ctx],
	)
	if (!translator) {
		// de lange uitleg alleen in dev: in de bouw telt elke byte (perf-budget)
		throw new Error(
			import.meta.env.DEV
				? 'useT() needs the <I18nProvider> mounted in entry.server/entry.client above it'
				: 'useT',
		)
	}
	return translator
}
